/* eslint-disable @typescript-eslint/no-explicit-any */
import { context, SpanKind, SpanStatusCode, trace } from '@opentelemetry/api';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { AxiosErrorWithTraceId, ReqMeta } from './types';
import { injectTraceHeaders, sanitizeUrl } from './utils';

const INSTRUMENTED = Symbol.for('claimscore.otel.axios.instrumented');
const REQ_META = Symbol('claimscore.otel.reqmeta');

export function instrumentAxios(
  instance: AxiosInstance,
  opts?: { tracerName?: string },
) {
  if ((instance as any)[INSTRUMENTED]) return instance;
  (instance as any)[INSTRUMENTED] = true;

  const tracer = trace.getTracer(opts?.tracerName ?? 'services-axios');

  instance.interceptors.request.use((config) => {
    const method = (config.method ?? 'GET').toUpperCase();
    const baseURL = config.baseURL ?? '';
    const url = typeof config.url === 'string' ? config.url : '';
    const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;
    const safeUrl = sanitizeUrl(fullUrl);

    const span = tracer.startSpan(`${method} ${safeUrl}`, {
      kind: SpanKind.CLIENT,
      attributes: {
        'http.request.method': method,
        'url.full': safeUrl,
        'http.client': 'axios',
      },
    });

    const spanCtx = span.spanContext();
    const { traceId } = spanCtx;

    const ctx = trace.setSpan(context.active(), span);
    context.with(ctx, () => injectTraceHeaders(config));

    config.headers = {
      ...(config.headers as any),
      'x-trace-id': traceId,
    };

    (config as any)[REQ_META] = { span, traceId } satisfies ReqMeta;
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const meta = (response.config as any)?.[REQ_META] as ReqMeta | undefined;
      if (meta?.span) {
        meta.span.setAttribute('http.response.status_code', response.status);
        meta.span.setStatus({ code: SpanStatusCode.OK });
        meta.span.end();
      }
      return response;
    },
    (error) => {
      const config = error?.config as any;
      const meta = config?.[REQ_META] as ReqMeta | undefined;

      if (meta?.span) {
        const status = error?.response?.status;
        if (typeof status === 'number') {
          meta.span.setAttribute('http.response.status_code', status);
        }

        meta.span.recordException(error);
        meta.span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error?.message ?? 'Axios request failed',
        });
        meta.span.end();
      }
      if (meta?.traceId) {
        (error as AxiosErrorWithTraceId).traceId = meta.traceId;
      }

      return Promise.reject(error);
    },
  );

  return instance;
}
